import type { BidQrCodesModel, FileModel } from "api";
import InlineBtn from "components/shared/inlineBtn";
import { downloadURI } from "helpers/system";
import React, { useState } from "react";
import { alert } from "services/alert";
import { getQrCodes } from "sources/ebills";

interface QrCodesDownloadBtnProps {
    bidId: number;
    waybillId?: number;
}

type DownloadableFile = FileModel & { accessUrlLink: string };

const qrCodesToFiles = (data: BidQrCodesModel | false, waybillId?: number): DownloadableFile[] => {
    if (!data) {
        return [];
    }

    const qrCodeGroups =
        waybillId === undefined
            ? Object.values(data.waybillQrCodes || {})
            : [data.waybillQrCodes?.[String(waybillId)] || []];

    return qrCodeGroups.reduce<DownloadableFile[]>(
        (files, qrCodes) =>
            files.concat(
                qrCodes
                    .map((qrCode) => qrCode.file)
                    .filter((file): file is DownloadableFile => Boolean(file?.accessUrlLink)),
            ),
        [],
    );
};

const downloadFileByUrl = (file: DownloadableFile) =>
    fetch(file.accessUrlLink).then((response) => {
        if (!response.ok) {
            throw new Error("Failed to download file");
        }

        return response.blob().then((blob) => {
            const url = window.URL.createObjectURL(blob);
            downloadURI(url, file.originalFileName || file.fileName || "qr-code");
            window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        });
    });

/** Downloads QR-code files available for a bid or a specific waybill. */
const QrCodesDownloadBtn = ({ bidId, waybillId }: QrCodesDownloadBtnProps) => {
    const [loading, setLoading] = useState(false);

    const downloadQrCodes = () => {
        if (loading) {
            return;
        }
        setLoading(true);
        getQrCodes(bidId)
            .then((data) => {
                const files = qrCodesToFiles(data, waybillId);

                if (!files.length) {
                    alert({
                        type: "error",
                        title: "Ошибка",
                        message: "QR коды не найдены",
                    });
                    return [];
                }

                return Promise.all(files.map(downloadFileByUrl));
            })
            .catch(() =>
                alert({
                    type: "error",
                    title: "Ошибка",
                    message: "Не удалось скачать QR коды",
                }),
            )
            .finally(() => setLoading(false));
    };

    return (
        <div className={loading ? "loading loading--small" : ""}>
            <InlineBtn value="Скачать QR коды" cname="download" action={downloadQrCodes} disabled={loading} />
        </div>
    );
};

export default QrCodesDownloadBtn;
